import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import toast, { Toaster } from "react-hot-toast";
import { RootState } from "../../../redux/rootReducers";
import {
  EmptyListPage,
  ErrorSuccessModal,
  TableHeader,
  TableLoader,
} from "../../commons";
import { useLocation } from "react-router-dom";
import { resetSession } from "../../../redux/auth";
import {
  addCheckInScheduleFeedbackApiAction,
  getCheckInScheduleListAction,
  getCheckInScheduleManagerViewListAction,
  getSingleCheckInScheduleDetailsAction,
  resetCheckInScheduleList,
  resetCheckInScheduleManagerViewList,
  resetMySpaceError,
  resetMySpaceMessage,
  resetMySpaceSuccess,
  resetSingleCheckInScheduleDetails,
} from "../../../redux/mySpace";
import {
  AddCheckInScheduleFeedbackPayloadType,
  GetCheckInScheduleListPayloadType,
  GetSingleCheckInScheduleDetailsPayloadType,
} from "../../../commonTypes/mySpaceType";
import { LoginResponseType } from "../../../redux/auth/authTypes";
import CheckInScheduleTable from "./CheckInScheduleTable";
import { CHECK_IN_SCHEDULE_HEADERS } from "../../../constant/tableHeadersConstants/checkInScheduleListHeader";
import { TableHeadersType } from "../../../constant/tableHeadersConstants/tableHeadersType";
import CheckInFeedBackModal from "../../ThinksToDo/CheckInFeedBackModal";
import { GetCheckInDetailDataType } from "../../../redux/mySpace/mySpaceTypes";
import ScrollToTop from "../../commons/ScrollToTop";
import InfiniteLoader from "../../commons/InfiniteLoader/InfiniteLoader";

type AppProps = {
  token: string | null;
  fetching: boolean;
  singleCheckInScheduleFetching: boolean;
  success: number | null;
  error: string | null;
  message: string | null;
  statusCode: number | null;
  checkInScheduleTotalCount: number;
  checkInScheduleList: GetCheckInDetailDataType[] | [];
  loginData: LoginResponseType;
  feedbackAddedMessage: string | null;
  singleCheckInScheduleDetails: GetCheckInDetailDataType;
  dispatchResetSession: () => void;
  dispatchResetCheckInScheduleList: () => void;
  dispatchAddCheckInScheduleFeedbackApiAction: (
    data: AddCheckInScheduleFeedbackPayloadType
  ) => void;
  dispatchGetCheckInScheduleList: (
    data: GetCheckInScheduleListPayloadType
  ) => void;
  dispatchGetSingleCheckInScheduleDetailsAction: (
    data: GetSingleCheckInScheduleDetailsPayloadType
  ) => void;
  dispatchResetSingleCheckInScheduleDetails: () => void;
  dispatchResetMySpaceMessage: () => void;
  dispatchResetMySpaceError: () => void;
  dispatchResetMySpaceSuccess: () => void;
};
const AllCheckInList = ({
  token,
  fetching,
  success,
  error,
  message,
  statusCode,
  loginData,
  singleCheckInScheduleFetching,
  dispatchResetSession,
  dispatchGetCheckInScheduleList,
  dispatchResetCheckInScheduleList,
  checkInScheduleTotalCount,
  checkInScheduleList,
  dispatchGetSingleCheckInScheduleDetailsAction,
  dispatchResetSingleCheckInScheduleDetails,
  feedbackAddedMessage,
  singleCheckInScheduleDetails,
  dispatchAddCheckInScheduleFeedbackApiAction,
  dispatchResetMySpaceMessage,
  dispatchResetMySpaceError,
  dispatchResetMySpaceSuccess,
}: AppProps) => {
  const { pathname } = useLocation();
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [data, setData] = useState<GetCheckInDetailDataType[]>([]);
  const [checkInId, setCheckInId] = useState<string>("");
  const [checkInFeedbackModal, setCheckInFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState<string>("");
  const search = useLocation().search;
  const redirectNotificationId = new URLSearchParams(search).get(
    "notification_id"
  );

  useEffect(() => {
    dispatchResetCheckInScheduleList();
    dispatchResetMySpaceError();
    dispatchResetMySpaceMessage();
    dispatchResetMySpaceSuccess();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (checkInScheduleList?.length === 0) {
      setData(checkInScheduleList);
    } else if (offset > 0 && checkInScheduleList?.length > 0) {
      setData((prv: GetCheckInDetailDataType[]) => [
        ...prv,
        ...checkInScheduleList,
      ]);
    } else {
      setData(checkInScheduleList);
    }
  }, [checkInScheduleList]);

  useEffect(() => {
    if (token) {
      const finalData = {
        token,
        offset,
        limit,
        role: "manager",
        notificationId: redirectNotificationId,
      };
      if (checkInScheduleTotalCount) {
        if (checkInScheduleTotalCount > offset) {
          dispatchGetCheckInScheduleList(finalData);
        }
      } else {
        dispatchGetCheckInScheduleList(finalData);
      }
    }
  }, [offset]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [checkInScheduleList]);

  useEffect(() => {
    if (singleCheckInScheduleDetails?.feedback) {
      setFeedback(singleCheckInScheduleDetails?.feedback);
    } else {
      setFeedback("");
    }
  }, [singleCheckInScheduleDetails]);

  useEffect(() => {
    if (
      feedbackAddedMessage?.includes(
        "Check-in feedback details has been added successfully!"
      )
    ) {
      toast.success("Check-in feedback details has been added successfully!", {
        duration: 3000,
      });
      dispatchResetMySpaceError();
      dispatchResetMySpaceMessage();
      dispatchResetMySpaceSuccess();
      if (offset === 0 && token) {
        const finalData = {
          token,
          offset,
          limit,
          role: "manager",
          notificationId: redirectNotificationId,
        };
        dispatchGetCheckInScheduleList(finalData);
      } else {
        setOffset(0);
      }
    }
  }, [feedbackAddedMessage]);

  const handleCheckInFeedBackModal = (data?: string) => {
    setCheckInFeedbackModal(!checkInFeedbackModal);
    dispatchResetSingleCheckInScheduleDetails();
    if (data) {
      setCheckInId(data);
    } else {
      setCheckInId("");
    }
    if (token && data) {
      const finalData = {
        token,
        checkInId: data,
      };
      dispatchGetSingleCheckInScheduleDetailsAction(finalData);
    }
  };

  const togglePopup = () => {
    setCheckInFeedbackModal(!checkInFeedbackModal);
  };

  const onScroll = () => {
    if (!fetching) {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (
        offset < +checkInScheduleTotalCount &&
        !fetching &&
        Math.ceil((75 / 100) * scrollHeight) <= scrollTop + clientHeight
      ) {
        if (checkInScheduleList?.length > 0) {
          if (!fetching && offset < +checkInScheduleTotalCount) {
            setOffset(offset + limit);
          }
        }
      }
    }
  };

  const handleCheckInFeedBackSubmit = () => {
    if (token) {
      const finalData = {
        token,
        checkInId,
        feedback,
      };
      dispatchAddCheckInScheduleFeedbackApiAction(finalData);
    }
  };

  const handleErrorModal = () => {
    if (success === 0) {
      if (statusCode === 401) {
        return (
          <ErrorSuccessModal
            type="error"
            isOpen={!!error}
            title="Error"
            message="Session Expired. Please Login Again!"
            buttonText="Logout"
            onPress={() => dispatchResetSession()}
            statusCode={statusCode}
          />
        );
      } else if (error && statusCode !== 401) {
        return (
          <ErrorSuccessModal
            type="error"
            isOpen={!!error}
            title="Error"
            message={error}
            buttonText="OK"
            onPress={() => {
              dispatchResetMySpaceError();
              dispatchResetMySpaceMessage();
              dispatchResetMySpaceSuccess();
            }}
          />
        );
      }
    }
  };

  return (
    <>
      <div className="header">
        <div className="left-header">
          <div className="heading">All Check-Ins List</div>
          {!fetching && checkInScheduleTotalCount > 0 && (
            <div
              className="sub-heading"
              style={{ marginTop: "-22px" }}
            >{`Total ${
              checkInScheduleTotalCount ? checkInScheduleTotalCount : 0
            } check-in schedule found`}</div>
          )}
        </div>
      </div>
      {fetching ? (
        <TableLoader Rows={10} ColumnPerRow={6} />
      ) : checkInScheduleList?.length ? (
        <CheckInScheduleTable
          tableHeader={CHECK_IN_SCHEDULE_HEADERS?.filter(
            (header: TableHeadersType) => header?.value !== "employment_status"
          )}
          data={data}
          type={"AllCheckIn"}
          handleCheckInFeedBackModal={handleCheckInFeedBackModal}
          fetching={fetching}
        />
      ) : (
        !fetching && (
          <div className="p-5 mt-3">
            <EmptyListPage title="No List Found!" imageWidth="30%" />
          </div>
        )
      )}
      {fetching && offset > 0 && <div>{<InfiniteLoader />}</div>}

      {checkInFeedbackModal && singleCheckInScheduleDetails && (
        <CheckInFeedBackModal
          toggle={togglePopup}
          isOpen={checkInFeedbackModal}
          data={singleCheckInScheduleDetails}
          handleSubmitFeedback={handleCheckInFeedBackSubmit}
          loginData={loginData}
          fetching={singleCheckInScheduleFetching}
          feedback={feedback}
          setFeedback={setFeedback}
        />
      )}
      {handleErrorModal()}
      <Toaster />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  leaveAndWFHHistoryList: state.stcoTools.leaveAndWFHHistoryList,
  token: state.auth.token,
  fetching: state.mySpace.checkInScheduleFetching,
  singleCheckInScheduleFetching: state.mySpace.singleCheckInScheduleFetching,
  message: state.mySpace.message,
  success: state.mySpace.success,
  statusCode: state.mySpace.statusCode,
  error: state.mySpace.error,
  checkInScheduleList: state.mySpace.checkInScheduleManagerViewList,
  checkInScheduleTotalCount: state.mySpace.checkInScheduleManagerViewTotalCount,
  loginData: state.auth.loginData,
  singleCheckInScheduleDetails: state.mySpace.singleCheckInScheduleDetails,
  feedbackAddedMessage: state.mySpace.message,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchResetSession: () => dispatch(resetSession()),
  dispatchGetCheckInScheduleList: (data: GetCheckInScheduleListPayloadType) =>
    dispatch(getCheckInScheduleManagerViewListAction(data)),
  dispatchResetMySpaceSuccess: () => dispatch(resetMySpaceSuccess()),
  dispatchResetMySpaceMessage: () => dispatch(resetMySpaceMessage()),
  dispatchResetMySpaceError: () => dispatch(resetMySpaceError()),
  dispatchResetCheckInScheduleList: () =>
    dispatch(resetCheckInScheduleManagerViewList()),
  dispatchAddCheckInScheduleFeedbackApiAction: (
    data: AddCheckInScheduleFeedbackPayloadType
  ) => dispatch(addCheckInScheduleFeedbackApiAction(data)),
  dispatchGetSingleCheckInScheduleDetailsAction: (
    data: GetSingleCheckInScheduleDetailsPayloadType
  ) => dispatch(getSingleCheckInScheduleDetailsAction(data)),
  dispatchResetSingleCheckInScheduleDetails: () =>
    dispatch(resetSingleCheckInScheduleDetails()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AllCheckInList);
